/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { IconDownload, IconEye } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter";
import NormalSearch from "../../../components/common/NormalSearch";
import ListView from "../../../components/common/ListView";
import CommonModal from "../../../components/common/CommonModal";
import { T, useLanguage } from "../../../context/LanguageContext";
import { useFuelListData } from "../../../apis/fuelApis";
import formatDate from "../../../utilities/helper";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import useDebounce from "../../../utilities/useDebounce";
import Pagination from "../../../components/common/Pagination";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { useSelector } from "react-redux";
import { handleDownload } from "../../../utilities/downloadHelper";
import CardSkeleton from "../../../components/skeleton/CardSkeleton";

type FuelData = {
  id: string;
};

interface FuelRecord {
  driver_id: string;
  driver_code: string;
  vehicle_code: string;
  vehicle_id: string;
  fuel_date: string;
  liters: number;
  price: number;
  fuel_type: string;
  address: string;
  fuel_slip: string;
}

interface FuelProps {
  vehicleId?: string;
}

const Fuel: React.FC<FuelProps> = ({ vehicleId }) => {
  const { t } = useLanguage();
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [openFuelSlip, setOpenFuelSlip] = useState<boolean>(false);
  const [openImage, setOpenImage] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<FuelData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);
  const { isLoading, refetch, isFetching } = useFuelListData(
    pageNo,
    limit,
    searchFor,
    vehicleId
  );

  const data = useSelector((state: any) => state?.fuel?.data);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    if (data?.fuel) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item: any) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.fuel.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.fuel, selectedRows]);

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit, vehicleId]);

  const toggleFuelSlipModal = (image: string = "") => {
    setOpenFuelSlip(!openFuelSlip);
    setOpenImage(image);
  };

  // const handleDownloadImage = () => {
  //   if (!openImage) return;
  //   window.open(openImage, "_blank");
  // };

  const handleDownloadImage = () => {
    const a = document.createElement("a");
    a.href = openImage;
    a.target = "_blank";
    a.download = "odometer-image.jpg";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Driver Id", value: "driver_code", isShow: true },
    { text: "Vehicle Id", value: "vehicle_code", isShow: true },
    { text: "Fuel Date", value: "fuel_date", isShow: true },
    { text: "Liters", value: "liters", isShow: true },
    { text: "Cost", value: "price", isShow: true },
    { text: "Fuel Type", value: "fuel_type", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.fuel?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.fuel.some((c: any) => c.id === id)),
      );
    } else {
      const newSelections =
        data?.fuel
          ?.filter((c: any) => !selectedRows.includes(c.id))
          .map((c: any) => c.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.fuel?.length > 0 &&
                    data?.fuel?.every((c: any) => selectedRows.includes(c.id))
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                 bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                  top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (c: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(c.id)}
                  onChange={() => handleSelectRow(c.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: "S/N",
            render: (_: FuelRecord, i: number) =>
              (pageNo - 1) * limit + (i + 1),
          };
        case "driver_code":
          return {
            label: "Driver Id",
            render: (record: FuelRecord) => record.driver_code,
          };
        case "vehicle_code":
          return {
            label: "Vehicle Id",
            render: (record: FuelRecord) => record.vehicle_code,
          };
        case "fuel_date":
          return {
            label: "Fuel Date",
            render: (record: FuelRecord) => formatDate(record.fuel_date),
          };
        case "liters":
          return {
            label: "Liters",
            render: (record: FuelRecord) => record.liters,
          };
        case "price":
          return {
            label: "Cost",
            render: (record: FuelRecord) => record.price,
          };
        case "fuel_type":
          return {
            label: "Fuel Type",
            render: (record: FuelRecord) => record.fuel_type,
          };
        case "actions":
          return {
            label: "Action",
            render: (record: FuelRecord) => (
              <div className="text-center">
                <button
                  onClick={() => toggleFuelSlipModal(record.fuel_slip)}
                  className="p-2 border rounded-full bg-[#F7F6F2]"
                >
                  <IconEye size={22} />
                </button>
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Driver Id": c.driver_code,
    "Vehicle Id": c.vehicle_code,
    "Fuel Date": formatDate(c.fuel_date),
    Liters: c.liters,
    Cost: c.price,
    "Fuel Type": c.fuel_type,
  });

  if (openFuelSlip && openImage) {
    return (
      <div className="space-y-4 p-2">
        {/* Back Header */}
        <div className="flex items-center justify-between pb-2 border-b border-gray-100">
          <button
            onClick={() => {
              setOpenFuelSlip(false);
              setOpenImage(null);
            }}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#212121] transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="tabler-icon tabler-icon-arrow-left"
            >
              <path d="M5 12l14 0"></path>
              <path d="M5 12l6 6"></path>
              <path d="M5 12l6 -6"></path>
            </svg>
            <T>Back to Fuel Logs</T>
          </button>

          <button
            onClick={handleDownloadImage}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FBD037]/20 text-[#000000] hover:bg-[#FBD037]/30 text-xs font-semibold rounded-lg transition-colors border border-[#FBD037]/30"
          >
            <IconDownload size={14} />
            <T>Download</T>
          </button>
        </div>

        {/* Image Preview */}
        <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 overflow-hidden shadow-sm flex items-center justify-center relative">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <img
            src={openImage}
            alt={t("Fuel Slip Preview")}
            className="w-full rounded-2xl h-[350px] object-cover"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="">
        <div className="px-2">
          <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
            <T>Fuel</T>
          </h1>
        </div>

        <div>
          {isLoading || isFetching ? (
            <CardSkeleton rows={3} />
          ) : data?.fuel && data.fuel.length > 0 ? (
            <div className="flex flex-col py-2">
              <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 overflow-hidden shadow-sm relative">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                {data.fuel.map((record: any, idx: number) => (
                  <div
                    key={record.id || idx}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-5 border-b border-gray-100 last:border-b-0 gap-3 hover:bg-gray-50 transition-colors group cursor-pointer"
                    onClick={() => toggleFuelSlipModal(record.fuel_slip)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FBD037]/10 flex items-center justify-center border border-[#FBD037]/20 text-[#212121] group-hover:bg-[#FBD037] transition-colors shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="tabler-icon tabler-icon-gas-station sm:hidden"
                        >
                          <path d="M14 11h1a2 2 0 0 1 2 2v3a1.5 1.5 0 0 0 3 0v-7l-3 -3"></path>
                          <path d="M4 20v-14a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v14"></path>
                          <path d="M3 20l12 0"></path>
                          <path d="M18 7v1a1 1 0 0 0 1 1h1"></path>
                          <path d="M4 11l10 0"></path>
                        </svg>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="tabler-icon tabler-icon-gas-station hidden sm:block"
                        >
                          <path d="M14 11h1a2 2 0 0 1 2 2v3a1.5 1.5 0 0 0 3 0v-7l-3 -3"></path>
                          <path d="M4 20v-14a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v14"></path>
                          <path d="M3 20l12 0"></path>
                          <path d="M18 7v1a1 1 0 0 0 1 1h1"></path>
                          <path d="M4 11l10 0"></path>
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-0.5">
                          <h3 className="text-[#212121] font-bold text-base sm:text-lg truncate max-w-[110px] sm:max-w-[150px]">
                            {record.vehicle_name ||
                              record.vehicle_code ||
                              t("Vehicle")}
                          </h3>
                          <span className="text-[#FBD037] font-mono text-[10px] sm:text-xs font-semibold tracking-wide bg-[#FDF8E5] px-1.5 py-0.5 rounded">
                            {t(record.fuel_type || "Diesel")}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs sm:text-sm truncate max-w-[140px] sm:max-w-[185px]">
                          <T>Refilled at</T>{" "}
                          <span className="font-semibold text-gray-700">
                            {record.address || record.station_name || t("Station")}
                          </span>{" "}
                          • <T>Qty</T>:{" "}
                          <span className="font-semibold text-gray-700">
                            {record.liters || 0}L
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="text-start sm:text-end border-t sm:border-t-0 border-gray-100 pt-2 sm:pt-0 shrink-0 flex sm:flex-col justify-between items-center sm:items-end">
                      <p className="text-xl sm:text-2xl font-bold text-[#FBD037] tracking-tight">
                        <span className="text-xs sm:text-sm font-bold mr-0.5"><T>USD</T></span>
                        {Number(record.price || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-gray-400 text-xs sm:text-sm">
                        {formatDate(record.fuel_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <NoDataFound />
          )}
        </div>
        <div className="py-4 flex justify-center">
          {data?.totalPages > 1 && (
            <Pagination
              totalPages={data?.totalPages}
              currentPage={pageNo}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Fuel;
